import {
  Badge,
  Box,
  Checkbox,
  Flex,
  HStack,
  IconButton,
  Switch,
  Table,
  Text,
  Tooltip,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { MdDelete, MdEdit, MdListAlt, MdRefresh } from "react-icons/md";
import { knowledgeBaseService } from "services/knowledgeBaseService";
import { getThemeColor } from "theme/colors";
import { KbWebSourceDto } from "types/knowledgeBase";
import MoveFolderPopover from "./MoveFolderPopover";
import WebSourcePagesModal from "./WebSourcePagesModal";
import WebSourceStatusBadge from "./WebSourceStatusBadge";

interface WebSourcesTableProps {
  webSources: KbWebSourceDto[];
  onEdit: (webSource: KbWebSourceDto) => void;
  onDelete: (id: string) => void;
  onSync: (id: string) => void;
  onWebSourceUpdated?: (source: KbWebSourceDto) => void;
  isDeleting?: string;
  isSyncing?: string;
  /** Optional — if provided, shows "Move to folder" button */
  onMove?: (id: string, folderId: string | null) => Promise<void>;
  folders?: Array<{ id: string; name: string; parentName?: string }>;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleAll?: (checked: boolean) => void;
}

const WebSourcesTable = ({
  webSources,
  onEdit,
  onDelete,
  onSync,
  onWebSourceUpdated,
  isDeleting,
  isSyncing,
  onMove,
  folders,
  selectedIds,
  onToggleSelect,
  onToggleAll,
}: WebSourcesTableProps) => {
  const { t } = useTranslation();
  const [togglingGlobal, setTogglingGlobal] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [pagesSource, setPagesSource] = useState<KbWebSourceDto | null>(null);

  const handleToggleGlobal = async (source: KbWebSourceDto) => {
    setTogglingGlobal(source.id);
    setToggleError(null);
    try {
      const updated = await knowledgeBaseService.setWebSourceGlobal(
        source.id,
        !source.isGlobal,
      );
      onWebSourceUpdated?.(updated);
    } catch (err) {
      console.error("Failed to toggle global flag", err);
      setToggleError("Failed to update. Please try again.");
    } finally {
      setTogglingGlobal(null);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box overflowX="auto">
      {toggleError && (
        <Flex
          p={2}
          mb={2}
          borderRadius="md"
          bg={{ base: "red.50", _dark: "red.900" }}
        >
          <Text fontSize="sm" color={{ base: "red.600", _dark: "red.300" }}>
            {toggleError}
          </Text>
        </Flex>
      )}
      <Table.Root minW="700px">
        <Table.Header>
          <Table.Row>
            {onToggleAll && selectedIds && (
              <Table.ColumnHeader w="40px">
                <Checkbox
                  size="sm"
                  checked={
                    webSources.length > 0 &&
                    selectedIds.size === webSources.length
                      ? true
                      : selectedIds.size > 0
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={(e: {
                    checked: boolean | "indeterminate";
                  }) => onToggleAll(e.checked === true)}
                />
              </Table.ColumnHeader>
            )}
            <Table.ColumnHeader>
              {t("app.knowledgeBase.webSources.table.name")}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t("app.knowledgeBase.webSources.table.urls")}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t("app.knowledgeBase.webSources.table.status")}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t("app.knowledgeBase.webSources.table.pages")}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t("app.knowledgeBase.webSources.table.lastSynced")}
            </Table.ColumnHeader>
            <Table.ColumnHeader>Global</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">
              {t("app.common.actions")}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {webSources.map((source) => (
            <Table.Row
              key={source.id}
              draggable={!!onMove}
              onDragStart={(e) => {
                e.dataTransfer.setData("itemType", "webSource");
                e.dataTransfer.setData("itemId", source.id);
                e.dataTransfer.effectAllowed = "move";
              }}
              cursor={onMove ? "grab" : undefined}
              bg={
                selectedIds?.has(source.id)
                  ? { base: "blue.50", _dark: "blue.900" }
                  : undefined
              }
            >
              {onToggleSelect && selectedIds && (
                <Table.Cell w="40px" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    size="sm"
                    checked={selectedIds.has(source.id)}
                    onCheckedChange={() => onToggleSelect(source.id)}
                  />
                </Table.Cell>
              )}
              <Table.Cell>
                <Box>
                  <HStack gap={2} mb={source.errorMessage ? 1 : 0}>
                    <Text fontWeight="medium" truncate maxW="160px">
                      {source.name}
                    </Text>
                    {source.isGlobal && (
                      <Badge colorPalette="purple" size="sm" variant="subtle">
                        Global
                      </Badge>
                    )}
                    {source.folderName && (
                      <Badge colorPalette="gray" size="sm" variant="subtle">
                        {source.folderName}
                      </Badge>
                    )}
                  </HStack>
                  {source.errorMessage && (
                    <Tooltip content={source.errorMessage}>
                      <Text fontSize="xs" color="red.500" truncate maxW="200px">
                        {source.errorMessage}
                      </Text>
                    </Tooltip>
                  )}
                </Box>
              </Table.Cell>
              <Table.Cell>
                <Tooltip content={source.seedUrls.join("\n")}>
                  <Text
                    color={getThemeColor("text.secondary")}
                    truncate
                    maxW="200px"
                  >
                    {t("app.knowledgeBase.webSources.urlsCount", {
                      count: source.seedUrls.length,
                    })}
                  </Text>
                </Tooltip>
              </Table.Cell>
              <Table.Cell>
                <WebSourceStatusBadge status={source.status} />
              </Table.Cell>
              <Table.Cell>
                <Text color={getThemeColor("text.secondary")}>
                  {source.pagesIndexed !== undefined &&
                  source.pagesIndexed !== null
                    ? `${source.pagesIndexed} / ${source.maxPages}`
                    : "-"}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text color={getThemeColor("text.secondary")}>
                  {formatDate(source.lastSyncedAt)}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Tooltip
                  content={
                    source.isGlobal ? "Included in all widgets" : "Make global"
                  }
                >
                  <Switch
                    size="sm"
                    colorPalette="purple"
                    checked={source.isGlobal}
                    onCheckedChange={() => handleToggleGlobal(source)}
                    disabled={togglingGlobal === source.id}
                  />
                </Tooltip>
              </Table.Cell>
              <Table.Cell>
                <HStack gap={1} justify="flex-end">
                  {onMove && folders && (
                    <MoveFolderPopover
                      folders={folders}
                      currentFolderId={source.folderId}
                      onMove={(fid) => onMove(source.id, fid)}
                    />
                  )}
                  <Tooltip
                    content={t(
                      "app.knowledgeBase.webSources.actions.viewPages",
                    )}
                  >
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={t(
                        "app.knowledgeBase.webSources.actions.viewPages",
                      )}
                      onClick={() => setPagesSource(source)}
                    >
                      <MdListAlt />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    content={t("app.knowledgeBase.webSources.actions.sync")}
                  >
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={t(
                        "app.knowledgeBase.webSources.actions.sync",
                      )}
                      onClick={() => onSync(source.id)}
                      loading={isSyncing === source.id}
                    >
                      <MdRefresh />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    content={t("app.knowledgeBase.webSources.actions.edit")}
                  >
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={t(
                        "app.knowledgeBase.webSources.actions.edit",
                      )}
                      onClick={() => onEdit(source)}
                    >
                      <MdEdit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    content={t("app.knowledgeBase.webSources.actions.delete")}
                  >
                    <IconButton
                      variant="ghost"
                      size="sm"
                      colorPalette="red"
                      aria-label={t(
                        "app.knowledgeBase.webSources.actions.delete",
                      )}
                      onClick={() => onDelete(source.id)}
                      disabled={isDeleting === source.id}
                      loading={isDeleting === source.id}
                    >
                      <MdDelete />
                    </IconButton>
                  </Tooltip>
                </HStack>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>

      <WebSourcePagesModal
        isOpen={pagesSource !== null}
        onClose={() => setPagesSource(null)}
        webSource={pagesSource}
      />
    </Box>
  );
};

export default WebSourcesTable;
